import I18n from 'I18n';
import { DashworksCitationTypename, LegacyCitationTypename, WebPageCitationTypename, CrmCitationTypename } from '../types/Sources';
import { getAppInfo } from './appUtils';
import Raven from 'raven-js';
// Helper functions to extract properties safely
const getSourceName = citation => {
  switch (citation.__typename) {
    case LegacyCitationTypename:
      return citation.sourceName;
    case DashworksCitationTypename:
      return citation.source;
    default:
      return undefined;
  }
};
const getMetadata = citation => {
  switch (citation.__typename) {
    case DashworksCitationTypename:
      return {
        url: citation.url,
        author: citation.author,
        sourceDate: citation.sourceCreatedAt
      };
    case LegacyCitationTypename:
      return citation.metadata;
    case WebPageCitationTypename:
    case CrmCitationTypename:
      return {
        url: citation.url
      };
    default:
      return undefined;
  }
};
export const getAppInfoForSource = source => {
  switch (source.__typename) {
    case DashworksCitationTypename:
      {
        const dashworksSource = source;
        const appName = dashworksSource.source;
        const appInfo = getAppInfo(appName, dashworksSource.url);
        return appInfo;
      }
    case LegacyCitationTypename:
      {
        const legacySource = source;
        const appName = legacySource.sourceName;
        const appInfo = getAppInfo(appName, legacySource.metadata.url);
        return appInfo;
      }
    default:
      return undefined;
  }
};
const getSourceConfigForDashworksSource = source => {
  const appInfo = getAppInfoForSource(source);
  return {
    source,
    icon: appInfo ? {
      url: appInfo.iconUrl,
      alt: appInfo.displayName
    } : undefined,
    primaryText: appInfo === null || appInfo === void 0 ? void 0 : appInfo.displayName,
    secondaryText: source.title
  };
};

// Default configuration for unknown source types
const DEFAULT_SOURCE_CONFIG = {
  getSectionTitle: () => undefined,
  generateConfig: source => ({
    source,
    primaryText: source.title
  })
};

// Configuration for different source types
const SOURCE_TYPE_CONFIGS = {
  [DashworksCitationTypename]: {
    getSectionTitle: () => I18n.text('connectedApps.apps'),
    generateConfig: source => {
      return getSourceConfigForDashworksSource(source);
    }
  },
  [CrmCitationTypename]: {
    getSectionTitle: () => 'CRM data',
    generateConfig: source => {
      return DEFAULT_SOURCE_CONFIG.generateConfig(source);
    }
  },
  [WebPageCitationTypename]: {
    getSectionTitle: () => 'Websites',
    generateConfig: source => {
      return DEFAULT_SOURCE_CONFIG.generateConfig(source);
    }
  },
  [LegacyCitationTypename]: {
    getSectionTitle: source => {
      const legacySource = source;
      if (legacySource.type === 'APP') {
        return I18n.text('connectedApps.apps');
      }
      return DEFAULT_SOURCE_CONFIG.getSectionTitle(source);
    },
    generateConfig: source => {
      const legacySource = source;
      if (legacySource.type === 'APP') {
        return getSourceConfigForDashworksSource(source);
      }
      return DEFAULT_SOURCE_CONFIG.generateConfig(source);
    }
  }
  // Add more source types here as needed
};

/**
 * Gets icon configuration for a citation based on its type and sourceName
 */
export const getSourceIcon = item => {
  const appInfo = getAppInfoForSource(item);
  if (!appInfo) {
    return undefined;
  }
  return {
    url: appInfo.iconUrl,
    alt: appInfo.displayName
  };
};

/**
 * Gets display name for a citation based on its sourceName
 */
export const getSourceDisplayName = item => {
  const appInfo = getAppInfoForSource(item);
  return (appInfo === null || appInfo === void 0 ? void 0 : appInfo.displayName) || '';
};
const getFormattedSourceDate = timestamp => {
  // Robust timestamp format detection based on digit count
  // Seconds: typically 10 digits, Milliseconds: typically 13 digits
  const timestampInMs = timestamp.toString().length <= 10 ? timestamp * 1000 // Convert seconds to milliseconds
  : timestamp; // Already in milliseconds

  const formattedDate = I18n.moment.userTz(timestampInMs).format('ll');
  return formattedDate;
};

/**
 * Gets badges for a citation based on its type and metadata
 */
export const getSourceBadges = item => {
  const badges = [];
  switch (item.__typename) {
    case DashworksCitationTypename:
      {
        var _dashworksSource$auth;
        const dashworksSource = item;
        if ((_dashworksSource$auth = dashworksSource.author) !== null && _dashworksSource$auth !== void 0 && _dashworksSource$auth.name) {
          badges.push({
            text: dashworksSource.author.name,
            tooltip: 'Author'
          });
        }
        if (dashworksSource.sourceCreatedAt) {
          badges.push({
            text: getFormattedSourceDate(dashworksSource.sourceCreatedAt),
            tooltip: 'Created at'
          });
        }
        break;
      }
    case LegacyCitationTypename:
      {
        const legacySource = item;
        if (legacySource.type === 'APP') {
          var _legacySource$metadat;
          if ((_legacySource$metadat = legacySource.metadata.author) !== null && _legacySource$metadat !== void 0 && _legacySource$metadat.name) {
            badges.push({
              text: legacySource.metadata.author.name,
              tooltip: 'Author'
            });
          }
        }
        if (legacySource.metadata.sourceDate) {
          badges.push({
            text: getFormattedSourceDate(legacySource.metadata.sourceDate),
            tooltip: 'Created at'
          });
        }
        break;
      }
    default:
      break;
  }
  return badges;
};

// === SOURCES LISTING UTILITIES (for Sources component) ===

/**
 * Creates a configuration object for a citation based on its type
 */
export const createSourceConfig = source => {
  const type = source.__typename;
  const typeConfig = SOURCE_TYPE_CONFIGS[type] || DEFAULT_SOURCE_CONFIG;
  return typeConfig.generateConfig(source);
};

/**
 * Creates source configurations for all citations
 */
export const createSourcesConfig = sources => {
  return sources.map(createSourceConfig);
};

/**
 * Groups source configurations by type and creates sections
 */
export const generateSourceSections = configs => {
  const groupedByType = configs.reduce((acc, config) => {
    const type = config.source.__typename;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(config);
    return acc;
  }, {});
  const sections = [];
  Object.entries(groupedByType).forEach(([type, sourcesOfType]) => {
    if (sourcesOfType.length > 0) {
      const typeConfig = SOURCE_TYPE_CONFIGS[type] || DEFAULT_SOURCE_CONFIG;
      sections.push({
        title: typeConfig.getSectionTitle(sourcesOfType[0].source),
        sources: sourcesOfType
      });
    }
  });
  return sections;
};

/**
 * Gets icon configurations for button icons
 */
export const getButtonIconConfigs = (configs, maxIcons = 3) => {
  const iconConfigs = [];
  const seenUrls = new Set();
  for (const config of configs) {
    if (iconConfigs.length >= maxIcons) {
      break;
    }
    if (config.icon && !seenUrls.has(config.icon.url)) {
      seenUrls.add(config.icon.url);
      iconConfigs.push(config.icon);
    }
  }
  return iconConfigs;
};
export const handleSourceClick = data => {
  if (!data) {
    return;
  }
  const metadata = getMetadata(data);
  if (!(metadata !== null && metadata !== void 0 && metadata.url)) {
    return;
  }
  try {
    window.open(metadata.url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    const sourceName = getSourceName(data);
    Raven.captureException(error, {
      extra: {
        component: 'Sources',
        action: 'openAppSource',
        url: metadata.url,
        sourceName
      }
    });
  }
};